import shopifyLiquid from "@shopify/prettier-plugin-liquid";
import { LRUCache } from "lru-cache";
import type { Options } from "prettier";
import parserBabel from "prettier/parser-babel";
import parserGraphql from "prettier/parser-graphql";
import parserTypescript from "prettier/parser-typescript";
import prettier from "prettier/standalone";
import type { StringChunk } from "./code/typescriptTemplate";

const cache = new LRUCache<string, string>({ max: typeof window == "undefined" ? 1000 : 500 });

/** De-indents a string for presentation to a human */
export const dedent = (str: string) => {
  const lines = str.split("\n");
  const indent = Math.min(...lines.filter((line) => line.trim().length > 0).map((line) => line.match(/^\s*/)?.[0].length ?? 0)) ?? 0;
  if (lines[0].trim().length == 0) lines.shift();
  if (lines[lines.length - 1]?.trim().length == 0) lines.pop();

  return lines.map((line) => line.slice(indent).trimEnd()).join("\n");
};

export const prettifyCodeExample = (code: string | StringChunk, language: string, printWidth?: number) => {
  code = String(code);
  const cacheKey = `${code}:${language}:${printWidth}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  let parser: string;
  const options: Options = {};
  code = dedent(code);

  switch (language) {
    case "liquid": {
      parser = "liquid-html";
      break;
    }
    case "graphql": {
      parser = "graphql";
      break;
    }
    case "react":
    case "javascript": {
      parser = "babel";
      break;
    }
    case "json": {
      parser = "json5";
      options.quoteProps = "preserve";
      options.trailingComma = "none";
      break;
    }
    case "typescript": {
      parser = "typescript";
      break;
    }
    default: {
      return code;
    }
  }

  const formattedCode = prettier
    .format(code, {
      parser,
      plugins: [parserGraphql, parserBabel, parserTypescript, shopifyLiquid],
      printWidth,
      ...options,
    })
    .trimEnd();

  cache.set(cacheKey, formattedCode);
  return formattedCode;
};

export const safePrettifyCodeExample = (code: string | StringChunk, language: string, printWidth?: number) => {
  try {
    return prettifyCodeExample(code, language, printWidth);
  } catch (e) {
    return code.toString();
  }
};
